import { MaterialCommunityIcons } from "@expo/vector-icons";
import { formatInTz } from "@taskhealth/shared";
import { Tabs, usePathname, useRouter, useSegments } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchVisits } from "@/lib/api";
import { useNotification } from "@/lib/notifications";
import { colors, fonts } from "@/lib/theme";
import { fetchUnreadCount } from "@/lib/ticket-api";
import { useWebSocketEvent } from "@/lib/websocket";

export default function TabsLayout() {
    const [unreadCount, setUnreadCount] = useState(0);
    const pathname = usePathname();
    const segments = useSegments();
    const segmentsRef = useRef(segments);
    segmentsRef.current = segments;
    const router = useRouter();
    const { showNotification } = useNotification();

    const loadUnread = useCallback(() => {
        fetchUnreadCount()
            .then((r) => setUnreadCount(r.unreadCount))
            .catch(() => {});
    }, []);

    // Refresh on mount, tab navigation, and ticket WS events
    useEffect(() => {
        loadUnread();
    }, [loadUnread, pathname]);

    useWebSocketEvent("ticket:message", (data) => {
        loadUnread();

        // Show toast only when NOT on chat screens
        const onChatScreen =
            segmentsRef.current.includes("support" as never) ||
            segmentsRef.current.includes("tickets" as never);
        if (onChatScreen) {
            return;
        }
        const sender = data.message.senderName ?? "New message";
        const preview = (data.message.content ?? "").trim().slice(0, 80);
        const text = preview ? `${sender}: ${preview}` : sender;
        showNotification(text, "info", () => {
            router.push("/tickets/" + data.ticketId);
        });
    });

    useWebSocketEvent("visit:updated", (data) => {
        if (data.action !== "request_accepted" && data.action !== "request_rejected") {
            return;
        }

        // Fetch visit details for patient name + start time
        fetchVisits()
            .then((visits) => {
                const visit = visits.find((v) => String(v.id) === String(data.visitId));
                const patient = visit
                    ? `${visit.patientFirstName} ${visit.patientLastName}`
                    : "Visit";
                // `visit.startTime` is naive local (no tz) — API tags it with a
                // phantom `Z`, so render in UTC to preserve the visit's wall-clock.
                const time = visit?.startTime
                    ? formatInTz(new Date(visit.startTime), "UTC", "MMM d, h:mm a")
                    : null;
                const details = time ? `${patient} — ${time}` : patient;

                if (data.action === "request_accepted") {
                    showNotification(`Request accepted: ${details}`, "approved", () => {
                        router.push("/visits");
                    });
                } else {
                    showNotification(`Request not confirmed: ${details}`, "rejected", () => {
                        router.push("/");
                    });
                }
            })
            .catch(() => {
                // Fallback if fetch fails
                if (data.action === "request_accepted") {
                    showNotification("Visit request accepted", "approved", () => {
                        router.push("/visits");
                    });
                } else {
                    showNotification("Visit request was not confirmed", "rejected", () => {
                        router.push("/");
                    });
                }
            });
    });

    return (
        <Tabs
            screenOptions={{
                tabBarActiveTintColor: colors.teal400,
                tabBarInactiveTintColor: colors.textTertiary,
                tabBarStyle: {
                    backgroundColor: colors.surface,
                    borderTopColor: colors.border,
                    borderTopWidth: 1.5,
                    height: 72,
                    paddingBottom: 12,
                    paddingTop: 8,
                    elevation: 0,
                    shadowOpacity: 0
                },
                tabBarLabelStyle: {
                    fontSize: 9,
                    fontFamily: fonts.bold,
                    letterSpacing: 0.3
                },
                tabBarIconStyle: {
                    marginBottom: -2
                },
                headerStyle: {
                    backgroundColor: colors.surface,
                    elevation: 0,
                    shadowOpacity: 0,
                    borderBottomWidth: 1.5,
                    borderBottomColor: colors.border
                },
                headerTintColor: colors.textPrimary,
                headerTitleStyle: {
                    fontFamily: fonts.bold,
                    fontSize: 18,
                    color: colors.textPrimary
                }
            }}
        >
            <Tabs.Screen
                name="index"
                options={{
                    title: "Explore",
                    headerShown: false,
                    tabBarIcon: ({ color, focused }) => (
                        <MaterialCommunityIcons
                            name={focused ? "compass" : "compass-outline"}
                            size={24}
                            color={color}
                        />
                    )
                }}
            />
            <Tabs.Screen
                name="visits"
                options={{
                    title: "Scheduled",
                    headerShown: false,
                    tabBarIcon: ({ color, focused }) => (
                        <MaterialCommunityIcons
                            name={focused ? "calendar-clock" : "calendar-clock-outline"}
                            size={24}
                            color={color}
                        />
                    )
                }}
            />
            <Tabs.Screen
                name="payments"
                options={{
                    title: "Payments",
                    tabBarIcon: ({ color, focused }) => (
                        <MaterialCommunityIcons
                            name={focused ? "wallet" : "wallet-outline"}
                            size={24}
                            color={color}
                        />
                    )
                }}
            />
            <Tabs.Screen
                name="support"
                options={{
                    title: "Support",
                    tabBarBadge: unreadCount > 0 ? unreadCount : undefined,
                    tabBarBadgeStyle: {
                        backgroundColor: colors.error500,
                        fontSize: 10,
                        fontFamily: fonts.bold,
                        minWidth: 18,
                        height: 18,
                        borderRadius: 9
                    },
                    tabBarIcon: ({ color, focused }) => (
                        <MaterialCommunityIcons
                            name={focused ? "chat" : "chat-outline"}
                            size={24}
                            color={color}
                        />
                    )
                }}
            />
        </Tabs>
    );
}
